import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface ShippingPolicyProps {
  onBack: () => void;
}

export const ShippingPolicy: React.FC<ShippingPolicyProps> = ({ onBack }) => {
  useSEOMetaTags({
    title: 'Shipping Policy - Orcaseotools | Digital Service Delivery',
    description: 'Shipping Policy for Orcaseotools. Learn about our instant digital service delivery, order processing, and worldwide availability of our SEO and AEO audit tools.',
    keywords: 'shipping policy, digital delivery, instant access, SEO tools delivery, website audit delivery, Orcaseotools shipping',
    canonicalUrl: 'https://orcaseotools.com/shipping-policy',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Shipping Policy - Orcaseotools",
      "description": "Shipping Policy for Orcaseotools digital services",
      "url": "https://orcaseotools.com/shipping-policy",
      "inLanguage": "en-US"
    }
  });

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-6">
          <header>
            <h1 className="text-4xl font-bold mb-2">Shipping Policy</h1>
            <p className="text-slate-400 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </header>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">1. Digital Service Delivery</h2>
            <p className="text-slate-300 leading-relaxed">
              Orcaseotools, operated by Sofia Host LLC, provides 100% digital SEO and AEO tools and services. All our products are delivered electronically over the internet. There is no physical shipping required, and no physical goods will be sent to your address.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">2. Instant Access Upon Payment</h2>
            <p className="text-slate-300 leading-relaxed">
              Once your payment is successfully processed and confirmed by our payment gateway (Stripe), you will receive immediate access to the services you have purchased.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">3. Service Activation Process</h2>
            <p className="text-slate-300 leading-relaxed">
              Upon successful payment, an account is automatically created for you (if you don't already have one). You will receive an email confirmation with your account details, allowing you to instantly access your tools and services via your dashboard.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">4. Worldwide Availability</h2>
            <p className="text-slate-300 leading-relaxed">
              Our digital platform is available globally. There are no geographic restrictions, and our services can be accessed 24/7 from anywhere in the world, provided you have a stable internet connection.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">5. Consulting Session Delivery</h2>
            <p className="text-slate-300 leading-relaxed">
              If you purchase a consulting session, delivery happens digitally via Google Meet or a similar video conferencing platform. Sessions can be conveniently scheduled directly through your dashboard.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">6. Service Downtime & Maintenance</h2>
            <p className="text-slate-300 leading-relaxed">
              While we strive for 100% uptime and instant delivery, there may be occasional planned maintenance or unforeseen technical difficulties that could temporarily delay service access. In such rare cases, your subscription period will not be affected, and we will work to restore service access as quickly as possible.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">7. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have any questions about our Shipping Policy or need assistance with accessing your services, please contact us:
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
              <p className="text-slate-300"><strong>Company:</strong> Sofia Host LLC</p>
              <p className="text-slate-300"><strong>Address:</strong> 1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</p>
              <p className="text-slate-300"><strong>Email:</strong> contact@orcaseotools.com</p>
              <p className="text-slate-300"><strong>Phone (WhatsApp):</strong> +1 (606) 557-7288</p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Thank you for choosing <strong>Orcaseotools</strong>. We aim to provide exceptional digital services with instant delivery.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};
